/**
 * Inline SVG icons.
 *
 * Hand-rolled, no icon library; every external dep counts against
 * the <50kb gz target. Each icon inherits `currentColor` so styling
 * lives entirely in CSS.
 */

export function ChatIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Send (paper plane). Slightly tilted so it reads as motion. Used in
 * the composer when the operator has typed something and a send
 * action becomes available.
 */
export function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12 20 4l-4 16-4-7-8-1z" />
    </svg>
  );
}

/**
 * Refresh / reset arrow. Used by the small "new conversation" link in
 * the header overflow.
 */
export function ResetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 15.3-6.4L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.3 6.4L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

/**
 * Microphone glyph. Used at two sizes: 18px next to the composer
 * (voice-first affordance) and 36px inside the voice-modal call
 * surface. The icon scales via the parent's font-size / explicit
 * width.
 */
export function MicIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M19 11v1a7 7 0 0 1-14 0v-1" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

/**
 * Paperclip glyph for the attach-photo button in the composer.
 * Slightly off-vertical so it reads as a paperclip rather than a
 * generic clip; same stroke weight as the other composer icons.
 */
export function AttachIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

/**
 * Filled rounded square: the "end call" button inside the voice
 * modal. Reads instantly as "stop"; the navy fill plus rounded
 * corners keeps it premium rather than alarming.
 */
export function StopSquareIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

/**
 * Left-pointing chevron. Used by the chat-state header's back button
 * which returns the visitor to the welcome card without closing the
 * widget.
 */
export function BackArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/**
 * Right-pointing chevron. Used at the tail of every channel pill in
 * the welcome card so the affordance reads as "tap to act" rather
 * than passive label.
 */
export function ChevronRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * Envelope glyph for the email channel pill. Single-line stroke so
 * it sits in a 16px slot without looking heavy.
 */
export function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

/**
 * Phone handset glyph for the telephone channel pill. Same stroke
 * weight as MailIcon for visual consistency in the pill stack.
 */
export function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.97.36 1.92.7 2.83a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.25-1.27a2 2 0 0 1 2.11-.45c.91.34 1.86.57 2.83.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/**
 * WhatsApp speech-bubble glyph. Plain outline rather than the
 * official WhatsApp green logo to keep the welcome card visually
 * calm and on-brand; the pill colour communicates the channel.
 */
export function WhatsAppIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a8.5 8.5 0 0 1-12.83 7.34L3 21l1.66-5.17A8.5 8.5 0 1 1 21 12z" />
      <path d="M8.5 9.5c0 3.5 2.5 6 6 6 .55 0 .82-.66.42-1.05l-1.2-1.2a.6.6 0 0 0-.7-.1l-.9.5a4 4 0 0 1-2-2l.5-.9a.6.6 0 0 0-.1-.7l-1.2-1.2c-.39-.4-1.05-.13-1.05.42z" />
    </svg>
  );
}
